import { DeviceInfo } from '@/hooks/useBrowserDevices';
import type { DeviceOption } from '@models/meeting-models'
import { LocalStorageKeys } from './storageKeys'

export function mapDevicesToOptions({ devices }: { devices: { deviceId: string; label: string }[] }): DeviceOption[] {
  return devices.map((device) => ({
    id: device.deviceId,
    label: device.label
  }))
}

export function extractDeviceId({ device }: { device: unknown }): string | null {
  if (!device) return null

  if (typeof device === 'string') return device

  if (
    typeof device === 'object' &&
    'deviceId' in device &&
    typeof (device as { deviceId?: unknown }).deviceId === 'string'
  ) {
    return (device as { deviceId: string }).deviceId
  }

  return null
}

export function formatDeviceError({
  error,
  deviceType,
  operation
}: {
  error: unknown
  deviceType: 'microphone' | 'camera' | 'speaker'
  operation: 'select' | 'start' | 'refresh'
}): string {
  const deviceTypeLabel = {
    microphone: 'le microphone',
    camera: 'la caméra',
    speaker: 'la sortie audio'
  }[deviceType]

  const operationLabel = {
    select: 'sélectionner',
    start: 'démarrer',
    refresh: 'rafraîchir'
  }[operation]

  const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'

  return `Unable to ${operationLabel} ${deviceTypeLabel}: ${errorMessage}`
}

// Helper function to restore or auto-select a device
export const restoreOrAutoSelectDevice = (
  savedDeviceId: string | undefined,
  availableDevices: DeviceInfo[],
  currentDeviceId: string | undefined
): string | undefined => {
  // Restore saved device if it still exists
  if (savedDeviceId && availableDevices.some((d) => d.id === savedDeviceId)) {
    return savedDeviceId
  }
  // Auto-select first device if no saved selection or saved device not found
  if (availableDevices.length > 0 && !currentDeviceId) {
    return availableDevices[0].id
  }
  return currentDeviceId
}

type StoredDeviceSelection = {
  audioInput?: string
  audioOutput?: string
  videoInput?: string
}

export const loadDeviceSelection = (): StoredDeviceSelection => {
  try {
    const stored = localStorage.getItem(LocalStorageKeys.devicePreferences)
    if (stored) {
      const parsed = JSON.parse(stored);

      // Check if the parsed object is valid
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const selection: StoredDeviceSelection = {};

        if (typeof parsed.audioInput === 'string') {
          selection.audioInput = parsed.audioInput;
        }
        if (typeof parsed.audioOutput === 'string') {
          selection.audioOutput = parsed.audioOutput;
        }
        if (typeof parsed.videoInput === 'string') {
          selection.videoInput = parsed.videoInput;
        }

        return selection;
      }
    }
  } catch (err) {
    console.error('[useBrowserDevices] Failed to load device selection from localStorage:', err)
  }
  return {}
}

export const saveDeviceSelection = (selection: StoredDeviceSelection) => {
  try {
    localStorage.setItem(LocalStorageKeys.devicePreferences, JSON.stringify(selection))
  } catch (err) {
    console.error('[useBrowserDevices] Failed to save device selection to localStorage:', err)
  }
}